import type { BusinessUnit, BusinessUnitListParams } from "@asap/shared/schemas/business-unit";
import { isAbortError } from "@asap/shared/utils/error";
import { useApi } from "~/composables/useApi";
import { useUserMe } from "~/components/user/composables/useUser";

export const useBusinessUnit = () => {
  const isInBusinessUnit = ref(false);

  const { invoke } = useApi();
  const { isSupportRole, isDeveloper } = useUserMe();

  const getBusinessUnits = async (
    params?: BusinessUnitListParams,
    signal?: AbortSignal
  ): Promise<{ data: BusinessUnit[]; count: number }> => {
    const response = await invoke({
      path: "business-unit",
      method: "GET",
      query: params,
      signal,
    });

    if (!response.success) {
      if (isAbortError(response.error)) return { data: [], count: 0 };
      throw new Error(response.error.message);
    }

    return { data: response.data.items, count: response.data.totalItems };
  };

  const selectSingleById = async (id: string, signal?: AbortSignal): Promise<BusinessUnit | null> => {
    const response = await invoke({
      path: "business-unit/:id",
      method: "GET",
      params: { id },
      signal,
    });
    if (!response.success) {
      if (isAbortError(response.error)) return null;
      throw new Error(response.error.message);
    }

    return response.data;
  };

  const canSwitchBusinessUnit = computed<boolean>(() => {
    return isSupportRole.value || isDeveloper.value || true;
  });

  return { selectSingleById, isInBusinessUnit, canSwitchBusinessUnit, getBusinessUnits };
};
